// src/pages/MyPurchases.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaDownload,
  FaEnvelope,
  FaExclamationTriangle,
  FaPrint,
  FaQrcode,
  FaReceipt,
  FaShoppingBag,
  FaSyncAlt,
  FaTicketAlt,
  FaTimesCircle,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  dateOnly,
  displayStatus,
  fetchOrders,
  getOrderItemTotal,
  getOrderItems,
  getOrderSubtotal,
  getOrderTitle,
  getOrderTotal,
  getOrderTps,
  getOrderTvq,
  getOrderVouchers,
  getTravelerSession,
  moneyCad,
} from '../lib/travelerData';
import {
  displayVoucherStatus,
  getVoucherEffectiveStatus,
  getVoucherRedemptionInfo,
  getVoucherStatusBadgeClass,
  getVoucherUrl,
} from '../lib/vouchers';
import { useTransactionI18n } from '../lib/transactionI18n';
import { downloadOrderReceiptPdf } from '../lib/pdfDocuments';
import ForfeoReservationPassPanel from '../components/network/ForfeoReservationPassPanel';

type LoadState = 'loading' | 'ready' | 'error';

const safeNumber = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const shortId = (value?: string | null) => {
  const raw = String(value || '');
  if (!raw) return 'FORFEO';
  return raw.slice(0, 8).toUpperCase();
};

const VoucherStatusIcon: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'active') return <FaCheckCircle />;
  if (status === 'redeemed') return <FaCheckCircle />;
  if (status === 'expired') return <FaClock />;
  if (status === 'cancelled' || status === 'canceled') return <FaTimesCircle />;
  return <FaQrcode />;
};

const MyPurchases: React.FC = () => {
  const navigate = useNavigate();
  const { tx, voucherStatus, orderStatus, language } = useTransactionI18n();
  const [state, setState] = useState<LoadState>('loading');
  const [orders, setOrders] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [receiptPdfLoading, setReceiptPdfLoading] = useState<string | null>(null);

  const loadOrders = async () => {
    setState('loading');
    setError(null);

    try {
      const traveler = await getTravelerSession();
      if (!traveler) {
        navigate('/login?redirect=/mes-achats');
        return;
      }

      setEmail(traveler.email);
      const result = await fetchOrders(traveler.uid, traveler.email);
      setOrders(result.data);
      setState('ready');
    } catch (err: any) {
      console.error('MyPurchases error:', err);
      setError(tx('purchasesLoadError'));
      setState('error');
    }
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paidOrders = useMemo(
    () => orders.filter((order) => ['paid', 'shipped', 'active'].includes(String(order.payment_status || order.status || '').toLowerCase())).length,
    [orders]
  );

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Header />
      <main className="pt-28 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-full px-4 py-2 text-xs font-black uppercase tracking-widest mb-4">
                <FaShoppingBag /> {tx('purchasesBadge')}
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-slate-900">{tx('purchasesTitle')}</h1>
              <p className="text-slate-500 mt-2">
                {tx('purchasesSubtitle')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/boutique" className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-2xl font-black hover:bg-slate-800">
                {tx('explore')} <FaArrowRight />
              </Link>
              <button onClick={loadOrders} className="inline-flex items-center justify-center gap-2 bg-white border border-slate-200 px-5 py-3 rounded-2xl font-bold text-slate-700 hover:bg-slate-100">
                <FaSyncAlt /> {tx('refresh')}
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-slate-100 rounded-3xl p-5 shadow-sm">
              <div className="text-xs font-black uppercase tracking-widest text-slate-400">{tx('orders')}</div>
              <div className="text-3xl font-black text-slate-900 mt-2">{orders.length}</div>
            </div>
            <div className="bg-white border border-slate-100 rounded-3xl p-5 shadow-sm">
              <div className="text-xs font-black uppercase tracking-widest text-slate-400">{tx('paidOrders')}</div>
              <div className="text-3xl font-black text-emerald-600 mt-2">{paidOrders}</div>
            </div>
            <div className="bg-white border border-slate-100 rounded-3xl p-5 shadow-sm">
              <div className="text-xs font-black uppercase tracking-widest text-slate-400">{tx('account')}</div>
              <div className="text-sm font-black text-slate-900 mt-3 truncate">{email || '—'}</div>
            </div>
          </div>

          {email && (
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-xs font-bold text-slate-500">
              <FaEnvelope /> {tx('account')} : {email}
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-100 text-red-700 p-4 rounded-2xl font-semibold flex items-start gap-3">
              <FaExclamationTriangle className="mt-1" /> {error}
            </div>
          )}

          {state === 'loading' ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
              <div className="w-12 h-12 border-4 border-slate-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-500 font-bold">{tx('loadingPurchases')}</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
              <FaShoppingBag className="mx-auto text-5xl text-slate-300 mb-5" />
              <h2 className="text-2xl font-black text-slate-900 mb-2">{tx('noPurchasesTitle')}</h2>
              <p className="text-slate-500 mb-8">{tx('noPurchasesSubtitle')}</p>
              <Link to="/boutique" className="inline-flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-xl font-black hover:bg-emerald-700">
                {tx('exploreShop')} <FaArrowRight />
              </Link>
            </div>
          ) : (
            <div className="space-y-5">
              {orders.map((order) => {
                const total = getOrderTotal(order);
                const subtotal = getOrderSubtotal(order);
                const tps = getOrderTps(order);
                const tvq = getOrderTvq(order);
                const status = orderStatus(order.payment_status || order.status || displayStatus(order.payment_status || order.status));
                const orderNumber = shortId(order.id || order.stripe_session_id);
                const items = getOrderItems(order);
                const vouchers = getOrderVouchers(order);
                const hasTaxDetails = safeNumber(subtotal) > 0 || safeNumber(tps) > 0 || safeNumber(tvq) > 0;

                return (
                  <article key={order.id || order.stripe_session_id} className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm hover:shadow-xl transition">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl">
                          <FaCreditCard />
                        </div>
                        <div>
                          <h2 className="font-black text-slate-900">Commande #{orderNumber}</h2>
                          <p className="text-sm text-slate-500">{tx('createdOn')} {dateOnly(order.created_at || order.paid_at)}</p>
                          <div className="mt-2 inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-[11px] font-black uppercase tracking-widest">
                            {status}
                          </div>
                        </div>
                      </div>

                      <div className="text-left lg:text-right">
                        <div className="text-2xl font-black text-slate-900">{moneyCad(total)}</div>
                        <div className="text-xs font-black uppercase tracking-widest text-slate-400">{tx('totalPaid')}</div>
                      </div>
                    </div>

                    <div className="mt-5 grid lg:grid-cols-[1.4fr_.8fr] gap-4">
                      <div className="rounded-2xl border border-slate-100 overflow-hidden">
                        <div className="bg-slate-50 px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
                          <FaTicketAlt /> {tx('items')}
                        </div>
                        {items.length > 0 ? (
                          <div className="divide-y divide-slate-100">
                            {items.slice(0, 6).map((item: any, index: number) => (
                              <div key={item.id || index} className="px-4 py-3 flex items-start justify-between gap-3">
                                <div>
                                  <div className="font-black text-slate-800 text-sm">{getOrderTitle(item)}</div>
                                  <div className="text-xs text-slate-400 mt-1">{tx('quantity')} : {item.quantity || item.q || 1}</div>
                                </div>
                                <div className="font-black text-slate-800 text-sm whitespace-nowrap">
                                  {moneyCad(getOrderItemTotal(item), { cents: true })}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="px-4 py-5 text-sm text-slate-500">
                            {tx('futureItemDetails')}
                          </div>
                        )}
                      </div>

                      <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                        <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-3">{tx('taxes')}</div>
                        {hasTaxDetails ? (
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between gap-3 text-slate-600">
                              <span>{tx('subtotal')}</span>
                              <strong>{moneyCad(subtotal, { cents: true })}</strong>
                            </div>
                            <div className="flex justify-between gap-3 text-slate-600">
                              <span>TPS (5%)</span>
                              <strong>{moneyCad(tps, { cents: true })}</strong>
                            </div>
                            <div className="flex justify-between gap-3 text-slate-600">
                              <span>TVQ (9,975%)</span>
                              <strong>{moneyCad(tvq, { cents: true })}</strong>
                            </div>
                            <div className="border-t border-slate-200 pt-3 flex justify-between gap-3 text-slate-900">
                              <span className="font-black">{tx('total')}</span>
                              <strong>{moneyCad(total, { cents: true })}</strong>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">
                            {tx('futureTaxDetails')}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-5 rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="text-xs font-black uppercase tracking-widest text-blue-700 flex items-center gap-2">
                          <FaQrcode /> {tx('voucherTickets')}
                        </div>
                        <span className="text-[11px] font-black text-blue-500">{vouchers.length} {vouchers.length > 1 ? tx('vouchers') : tx('voucher')}</span>
                      </div>

                      {vouchers.length > 0 ? (
                        <div className="grid md:grid-cols-2 gap-3">
                          {vouchers.map((voucher: any) => {
                            const effectiveStatus = getVoucherEffectiveStatus(voucher);
                            const redemption = getVoucherRedemptionInfo(voucher);
                            const statusBadge = getVoucherStatusBadgeClass(effectiveStatus);
                            const token = voucher.secure_token || voucher.token || '';
                            const canOpen = Boolean(token);

                            const content = (
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="font-black text-slate-900 text-sm truncate">{voucher.title || tx('voucherForfeo')}</div>
                                  <div className="text-xs text-slate-400 mt-1 font-mono break-all">{voucher.voucher_code}</div>
                                  <div className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest ${statusBadge}`}>
                                    <VoucherStatusIcon status={effectiveStatus} />
                                    {voucherStatus(effectiveStatus)}
                                  </div>
                                  {effectiveStatus === 'redeemed' && (
                                    <div className="mt-2 text-[11px] leading-5 text-slate-500 font-bold">
                                      {tx('usedOn')} {dateOnly(voucher.redeemed_at || redemption?.redeemed_at)}
                                      {redemption?.business_name ? ` · ${tx('validatedBy')} ${redemption.business_name}` : ''}
                                    </div>
                                  )}
                                  {effectiveStatus === 'expired' && (
                                    <div className="mt-2 text-[11px] leading-5 text-orange-700 font-bold">
                                      {tx('expiredPrefix')}{voucher.expires_at ? ` ${dateOnly(voucher.expires_at)}` : ''}
                                    </div>
                                  )}
                                  {(effectiveStatus === 'cancelled' || effectiveStatus === 'canceled') && (
                                    <div className="mt-2 text-[11px] leading-5 text-red-700 font-bold">
                                      {tx('voucherCancelled')}
                                    </div>
                                  )}
                                </div>
                                <FaQrcode className="text-blue-500 group-hover:scale-110 transition shrink-0" />
                              </div>
                            );

                            return canOpen ? (
                              <Link
                                key={voucher.id || voucher.secure_token || voucher.voucher_code}
                                to={getVoucherUrl(token)}
                                className="bg-white border border-blue-100 rounded-2xl p-4 hover:shadow-md transition group"
                              >
                                {content}
                              </Link>
                            ) : (
                              <div
                                key={voucher.id || voucher.voucher_code}
                                className="bg-white border border-blue-100 rounded-2xl p-4"
                              >
                                {content}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-sm text-blue-900/70">
                          {tx('noVoucherForOrder')}
                        </div>
                      )}
                    </div>

                    <ForfeoReservationPassPanel
                      compact
                      id={order.id || order.stripe_session_id}
                      kind="purchase"
                      title="Achat prêt pour le wallet Forfeo"
                      subtitle="Les billets, cartes cadeaux et confirmations sont préparés pour l’app mobile Forfeo et la validation QR."
                      className="mt-5"
                    />

                    <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                      <div className="text-xs text-slate-400">
                        {tx('session')} : {order.stripe_session_id ? String(order.stripe_session_id).slice(0, 18) + '…' : tx('pendingText')}
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={async () => {
                            try {
                              setReceiptPdfLoading(String(order.id || order.stripe_session_id || orderNumber));
                              await downloadOrderReceiptPdf(order, language);
                            } finally {
                              setReceiptPdfLoading(null);
                            }
                          }}
                          disabled={receiptPdfLoading === String(order.id || order.stripe_session_id || orderNumber)}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-wait"
                        >
                          <FaDownload /> {receiptPdfLoading === String(order.id || order.stripe_session_id || orderNumber) ? tx('preparingPdf') : tx('downloadReceiptPdf')}
                        </button>
                        <button onClick={() => window.print()} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50">
                          <FaPrint /> {tx('print')}
                        </button>
                        <Link to="/service-client" className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-bold">
                          <FaReceipt /> {tx('support')}
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MyPurchases;
